import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import orientationService from '../../../api/services/orientationService';
import candidateService from '../../../api/services/candidateService';
import hrService from '../../../api/services/hrService';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import CustomAlert from '../../../components/common/CustomAlert';
import {
  ArrowLeft, Send, User, Calendar, Clock, MapPin,
  CheckCircle, XCircle, Users, Search
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { formatDate } from '../../../utils/stringUtils';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';

const SendOrientationInvites = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [inviteMessage, setInviteMessage] = useState('');
  const [error, setError] = useState(null);

  const [orientation, setOrientation] = useState(null);

  // Candidate State
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesPage, setCandidatesPage] = useState(1);
  const [candidatesLimit, setCandidatesLimit] = useState(10);
  const [candidatesTotal, setCandidatesTotal] = useState(0);
  const [candidatesTotalPages, setCandidatesTotalPages] = useState(0);

  // Employee State
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesPage, setEmployeesPage] = useState(1);
  const [employeesLimit, setEmployeesLimit] = useState(10);
  const [employeesTotal, setEmployeesTotal] = useState(0);
  const [employeesTotalPages, setEmployeesTotalPages] = useState(0);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const orientationDateTimeDisplay = (() => {
    if (!orientation?.date) return 'N/A';
    const dateObj = new Date(orientation.date);
    if (orientation.time) {
      const [hours, minutes] = orientation.time.split(':').map(num => parseInt(num, 10));
      dateObj.setHours(hours, minutes || 0, 0, 0);
    }
    return formatDate(dateObj, { includeTime: true }) || 'N/A';
  })();

  // Debounce search term with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset pages on new search
      setCandidatesPage(1);
      setEmployeesPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadOrientation();
  }, [id]);

  useEffect(() => {
    loadCandidates(debouncedSearchTerm, candidatesPage, candidatesLimit);
  }, [debouncedSearchTerm, candidatesPage, candidatesLimit]);

  useEffect(() => {
    loadEmployees(debouncedSearchTerm, employeesPage, employeesLimit);
  }, [debouncedSearchTerm, employeesPage, employeesLimit]);

  const loadOrientation = async () => {
    try {
      setLoading(true);
      setError(null);



      const result = await orientationService.getOrientationById(id);

      if (result && result.responseCode === 200 && result.data) {

        const orientationData = result.data.orientation;
        const orientationDateObj = new Date(orientationData.date);
        if (orientationData.time) {
          const [hours, minutes] = orientationData.time.split(':').map(num => parseInt(num, 10));
          orientationDateObj.setHours(hours, minutes || 0, 0, 0);
        }
        const formattedDateTime = formatDate(orientationDateObj, { includeTime: true }) || 'TBD';
        setOrientation(orientationData);
        setInviteMessage(`- Orientation session scheduled for ${formattedDateTime}
- Please arrive 10 minutes early
- Bring a valid ID and any required documents
- Dress code: Business casual`);

        // Load existing invites/attendees and pre-select them
        loadExistingInvites(id);
      } else {

        setError('Orientation not found');
        showError('Orientation not found');
      }
    } catch (error) {

      setError('Error loading orientation');
      showError('Error loading orientation');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingInvites = async (orientationId) => {
    try {

      const result = await orientationService.getOrientationAttendees(orientationId);

      if (result && result.responseCode === 200 && Array.isArray(result.data.attendees)) {
        const attendees = result.data.attendees;


        // Map attendees to the format used in selectedEmployees
        const mappedAttendees = attendees.map(att => ({
          id: att.id,
          name: att.name,
          email: att.email,
          department: att.department,
          isEmployee: att.isEmployee,
          inviteStatus: att.inviteStatus,
          isAlreadyInvited: true // Flag to distinguish already invited ones
        }));

        setSelectedEmployees(mappedAttendees);
      }
    } catch (error) {

    }
  };

  const loadCandidates = async (search = '', page = 1, limit = 10) => {
    try {
      setCandidatesLoading(true);


      const params = {
        page: page,
        limit: limit,
        status: 'hired',
        fields: 'name,firstName,lastName,department,email,phone,location,experience,skills,appliedDate,candidateId,_id,jobTitle,position,hireDate,joiningDate,jobIdString',
        view: 'pipeline',
        ...(search && search.trim() && { search: search.trim() })
      };

      const result = await candidateService.getCandidates(params);


      const candidatesData = result?.data?.candidates;
      const paginationData = result?.data?.pagination;

      if (!Array.isArray(candidatesData)) {

        showError('Failed to load hired candidates');
        return;
      }

      setCandidatesTotal(paginationData?.totalCandidates || 0);
      setCandidatesTotalPages(paginationData?.totalPages || 0);

      const mappedCandidates = candidatesData.map(candidate => {
        return {
          id: candidate.id,
          candidateId: candidate.candidateId,
          name: candidate.name,
          department: candidate.department,
          email: candidate.email,
          phone: candidate.phone,
          location: candidate.location,
          experience: candidate.experience,
          skills: candidate.skills,
          appliedDate: candidate.appliedDate,
          jobIdString: candidate.jobIdString,
          isEmployee: false
        };
      });


      setCandidates(mappedCandidates);
    } catch (error) {

      showError('Error loading hired candidates');
    } finally {
      setCandidatesLoading(false);
    }
  };

  const loadEmployees = async (search = '', page = 1, limit = 10) => {
    try {
      setEmployeesLoading(true);


      // Get current month start and end dates
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

      const queryString = new URLSearchParams({
        joiningDateStart: startOfMonth,
        joiningDateEnd: endOfMonth,
        page: page,
        limit: limit,
        ...(search && { search })
      }).toString();

      const result = await hrService.getEmployees(queryString);


      if (result && result.success && result.data && Array.isArray(result.data.employees)) {
        setEmployeesTotal(result.data.pagination?.totalEmployees || 0);
        setEmployeesTotalPages(result.data.pagination?.totalPages || 0);

        const mappedEmployees = result.data.employees.map(emp => ({
          id: emp._id,
          employeeId: emp.employeeId,
          name: `${emp.personalInfo?.firstName || ''} ${emp.personalInfo?.lastName || ''}`.trim(),
          department: emp.employmentInfo?.department || 'N/A',
          email: emp.contactInfo?.personalEmail || (emp.user?.email) || 'N/A',
          phone: emp.contactInfo?.personalPhone || 'N/A',
          isEmployee: true
        }));
        setEmployees(mappedEmployees);
      } else {
        setEmployees([]);
        setEmployeesTotal(0);
        setEmployeesTotalPages(0);
      }
    } catch (error) {

      showError('Error loading employees');
    } finally {
      setEmployeesLoading(false);
    }
  };

  const [lastClickTime, setLastClickTime] = useState(0);

  const handleSelectPerson = useCallback((person) => {
    const now = Date.now();
    if (now - lastClickTime < 300) {
      return;
    }
    setLastClickTime(now);

    setSelectedEmployees(prev => {
      const personId = person.id;
      const isSelected = prev.find(p => p.id === personId);

      if (isSelected) {
        return prev.filter(p => p.id !== personId);
      } else {
        const totalCapacity = orientation?.capacity || 0;
        const acceptedCount = orientation?.attendeesCount || 0;
        const remainingSeats = Math.max(totalCapacity - acceptedCount - prev.length, 0);
        if (remainingSeats <= 0) {
          showError(`Capacity reached. ${acceptedCount}/${totalCapacity} seats already filled.`);
          return prev;
        }

        return [...prev, person];
      }
    });
  }, [lastClickTime, orientation?.capacity, showError]);

  const handleSelectAll = (type = 'candidates') => {
    const listToSelect = type === 'candidates' ? candidates : employees;

    // Check if all items in the current list are already selected
    const allSelected = listToSelect.every(item =>
      selectedEmployees.some(selected => selected.id === item.id)
    );

    if (allSelected) {
      // Deselect all items in the current list
      setSelectedEmployees(prev => prev.filter(p =>
        !listToSelect.some(item => item.id === p.id)
      ));
    } else {
      // Select all (respecting capacity)
      // Identify items in current list that are NOT yet selected
      const currentlySelectedIds = new Set(selectedEmployees.map(p => p.id));
      const itemsToAdd = listToSelect.filter(item => !currentlySelectedIds.has(item.id));

      // Check capacity
      const totalCapacity = orientation?.capacity || 0;
      const acceptedCount = orientation?.attendeesCount || 0;
      const currentTotalSelected = selectedEmployees.length;

      const remainingSeats = Math.max(totalCapacity - acceptedCount - currentTotalSelected, 0);

      if (remainingSeats <= 0) {
        showError(`Capacity reached. ${acceptedCount}/${totalCapacity} seats already filled.`);
        return;
      }

      const toAdd = itemsToAdd.slice(0, remainingSeats);
      if (toAdd.length < itemsToAdd.length) {
        showError(`Only ${remainingSeats} seats remaining. Added first ${toAdd.length}.`);
      }

      setSelectedEmployees(prev => [...prev, ...toAdd]);
    }
  };

  const handleRemovePerson = (personId) => {
    setSelectedEmployees(prev => prev.filter(p => p.id !== personId));
  };

  const handleSendInvites = async () => {
    if (selectedEmployees.length === 0) {
      showError('Please select at least one candidate to send invites');
      return;
    }

    // Enforce capacity before sending (accepted + selected <= capacity)
    const totalCapacity = orientation?.capacity || 0;
    const acceptedCount = orientation?.attendeesCount || 0;
    if (acceptedCount + selectedEmployees.length > totalCapacity) {
      const remaining = Math.max(totalCapacity - acceptedCount, 0);
      showError(`Capacity exceeded. Remaining seats: ${remaining}. Reduce selection and try again.`);
      return;
    }

    // Filter out people who were already invited (if we don't want to re-send to them)
    // Unless they are newly selected in this session.
    // For now, let's assume we only want to send to those who don't have isAlreadyInvited or whose inviteStatus is missing
    const newInvitees = selectedEmployees.filter(emp => !emp.isAlreadyInvited);

    if (newInvitees.length === 0) {
      toast.error('No new candidates selected to send invites to.');
      return;
    }

    setSending(true);

    // Use toast.promise for real-time toaster notification
    const invitePromise = orientationService.sendOrientationInvites(id, {
      employeeIds: newInvitees.map(emp => emp.id || emp._id).filter(id => id !== null && id !== undefined),
      inviteMessage
    });

    toast.promise(invitePromise, {
      loading: 'Sending orientation invites...',
      success: (result) => {
        setSending(false);
        if (result.data && result.data.sent > 0) {
          // Navigate after a short delay
          setTimeout(() => navigate('/orientation'), 2000);
          return `Invites sent successfully to ${result.data.sent} people`;
        } else {
          const errorMessages = result.data?.errorMessages || [];
          return `No invites sent. ${result.data?.errors || 0} errors occurred.`;
        }
      },
      error: (err) => {
        setSending(false);
        return err.response?.data?.responseMessage || err.message || 'Failed to send invites';
      }
    });

    try {
      await invitePromise;
    } catch (error) {

    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orientation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">❌</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Orientation</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <PermissionGate permission="onboarding" action="read">
            <Button onClick={loadOrientation} className="bg-agrosaf-green hover:bg-agrosaf-darkGreen">
              Try Again
            </Button>
          </PermissionGate>
        </div>
      </div>
    );
  }

  if (!orientation) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Orientation Not Found</h2>
        <p className="text-gray-600">The requested orientation session could not be found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          icon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Send Orientation Invites</h1>
          <p className="text-gray-600 mt-1 break-all overflow-hidden">Send invites for: <span className="capitalize">{orientation.title}</span></p>
        </div>
      </div>

      {/* Orientation Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Orientation Session Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center space-x-3">
            <Calendar className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Date & Time</p>
              <p className="text-sm text-gray-600">
                {orientationDateTimeDisplay}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Clock className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Duration</p>
              <p className="text-sm text-gray-600">{orientation.duration} hours</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Users className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">Capacity</p>
              <p className="text-sm text-gray-600">
                {selectedEmployees.length}/{orientation?.capacity || 0} people selected
              </p>
              <p className="text-xs text-gray-500">
                {(orientation?.capacity || 0) - selectedEmployees.length} spots remaining
              </p>
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-start space-x-3">
          <MapPin className="h-5 w-5 text-gray-400" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">Location</p>
            <p className="text-sm text-gray-600 whitespace-pre-wrap break-words">
              {typeof orientation.location === 'object' && orientation.location !== null
                ? (orientation.location.fullAddress || orientation.location.name)
                : orientation.location}
            </p>
          </div>
        </div>
      </div>

      {/* Invite Message */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
        <div className="space-y-4">
          <div>
            <textarea
              id="inviteMessage"
              value={inviteMessage}
              onChange={(e) => {
                // Allow all printable characters including punctuation, newlines, etc.
                setInviteMessage(e.target.value.slice(0, 1000));
              }}
              rows={12}
              maxLength={3000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent whitespace-pre-wrap"
              placeholder="Enter your invite message..."
              required
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {inviteMessage.length}/3000 characters
            </div>
          </div>

        </div>
      </div>

      {/* Global Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="relative">
          <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search employees and candidates..."
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
              setSearchTerm(value);
            }}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent text-gray-900 placeholder-gray-500"
          />
        </div>
      </div>

      {/* Employee Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Select New Employees (Joining This Month)</h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => handleSelectAll('employees')}
              className="text-sm font-medium text-agrosaf-green hover:text-agrosaf-green-hover"
            >
              {selectedEmployees.filter(p => p.isEmployee).length === employees.length && employees.length > 0
                ? 'Deselect All'
                : 'Select All'}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto min-h-[200px] max-h-[400px] overflow-y-auto">
          {employeesLoading ? (
            <div className="py-10 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-2"></div>
              <p className="text-gray-500">Loading new employees...</p>
            </div>
          ) : employees.length === 0 ? (
            <div className="py-10 text-center border-2 border-dashed border-gray-200 rounded-lg">
              <Users className="h-10 w-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No new employees joining this month found.</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                    <input
                      type="checkbox"
                      checked={selectedEmployees.filter(p => p.isEmployee).length === employees.length && employees.length > 0}
                      onChange={() => handleSelectAll('employees')}
                      className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employees.map((emp) => {
                  const isSelected = selectedEmployees.find(p => p.id === emp.id);
                  return (
                    <tr
                      key={emp.id}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectPerson(emp);
                      }}
                      className={`cursor-pointer transition-colors ${isSelected
                        ? 'bg-green-50 hover:bg-green-100'
                        : 'hover:bg-gray-50'
                        }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            onChange={() => handleSelectPerson(emp)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-full bg-agrosaf-green/10 flex items-center justify-center text-agrosaf-green font-medium text-sm mr-3">
                            {emp.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{emp.name}</div>
                            <div className="text-xs text-gray-500">{emp.employeeId}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {emp.department}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{emp.email}</div>
                        <div className="text-xs text-gray-500">{emp.phone}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {employees.length > 0 && (
            <PaginationControls
              border={false}
              currentPage={employeesPage}
              totalPages={employeesTotalPages}
              totalItems={employeesTotal}
              limit={employeesLimit}
              hasNextPage={employeesPage < employeesTotalPages}
              hasPrevPage={employeesPage > 1}
              onPageChange={setEmployeesPage}
              onLimitChange={setEmployeesLimit}
              limitOptions={[10, 25, 50, 100]}
            />
          )}
        </div>
        </div>
      </div>

      {/* Candidate Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Select Hired Candidates</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleSelectAll('candidates')}
            >
              {selectedEmployees.filter(p => !p.isEmployee).length === candidates.length && candidates.length > 0 ? 'Deselect All' : 'Select All'}
            </Button>
          </div>

        {candidatesLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-2"></div>
              <p className="text-gray-600">Loading candidates...</p>
            </div>
          </div>
        ) : candidates.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No hired candidates found</p>
            {searchTerm && (
              <p className="text-sm text-gray-400 mt-2">Try adjusting your search criteria</p>
            )}
            {!searchTerm && (
              <p className="text-sm text-gray-400 mt-2">No hired candidates available for orientation invites</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={candidates.length > 0 && selectedEmployees.filter(p => !p.isEmployee).length === candidates.length}
                        onChange={() => handleSelectAll('candidates')}
                        className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                      />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Candidate ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Department
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {candidates.map((candidate) => {
                  if (!candidate) return null;
                  const candidateId = candidate.id;
                  const isSelected = selectedEmployees.find(cand => cand.id === candidateId);
                  return (
                    <tr
                      key={candidateId}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectPerson(candidate);
                      }}
                      className={`cursor-pointer transition-colors ${isSelected
                        ? 'bg-green-50 hover:bg-green-100'
                        : 'hover:bg-gray-50'
                        }`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            onChange={() => handleSelectPerson(candidate)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900" title={candidate.candidateId || 'N/A'}>
                          {candidate.candidateId || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[160px]" title={candidate.name || 'N/A'}>
                          {candidate.name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-600 truncate max-w-[200px]" title={candidate.email || 'N/A'}>
                          {candidate.email || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-500 truncate max-w-[140px]" title={candidate.phone || 'N/A'}>
                          {candidate.phone || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-500 truncate max-w-[160px]" title={candidate.department || 'N/A'}>
                          {candidate.department || 'N/A'}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </div>

        {candidates.length > 0 && (
          <PaginationControls
            border={false}
            currentPage={candidatesPage}
            totalPages={candidatesTotalPages}
            totalItems={candidatesTotal}
            limit={candidatesLimit}
            hasNextPage={candidatesPage < candidatesTotalPages}
            hasPrevPage={candidatesPage > 1}
            onPageChange={setCandidatesPage}
            onLimitChange={(newLimit) => {
              setCandidatesLimit(newLimit);
              setCandidatesPage(1);
            }}
          />
        )}
      </div>

      {/* Selected Invitees */}
      {selectedEmployees.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Selected Invitees ({selectedEmployees.length})
          </h2>
          <div className="space-y-2">
            {selectedEmployees.map((person) => {
              if (!person) return null;
              const personId = person.id || person._id;
              return (
                <div key={personId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg overflow-hidden">
                  <div className="flex items-center space-x-3 flex-1 min-w-0 overflow-hidden">
                    <div className={`h-8 w-8 rounded-full ${person.isEmployee ? 'bg-blue-100 text-[#0F2942]' : 'bg-gray-100 text-gray-400'} flex items-center justify-center flex-shrink-0`}>
                      <User className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-gray-900 break-words overflow-hidden">{person.name || 'N/A'}</h4>
                        {person.isEmployee && (
                          <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 flex-shrink-0">
                            Employee
                          </span>
                        )}
                        {person.isAlreadyInvited && (
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${person.inviteStatus === 'accepted' ? 'bg-green-100 text-green-800' :
                            person.inviteStatus === 'declined' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                            {person.inviteStatus ? person.inviteStatus.charAt(0).toUpperCase() + person.inviteStatus.slice(1) : 'Invited'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 break-words overflow-hidden">{person.email || 'N/A'}</p>
                    </div>
                  </div>
                  {!person.isAlreadyInvited && (
                    <button
                      onClick={() => handleRemovePerson(personId)}
                      className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2"
                    >
                      <XCircle className="h-4 w-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-4">
        <Button
          variant="outline"
          onClick={() => navigate('/orientation')}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          icon={<Send className="h-4 w-4" />}
          onClick={handleSendInvites}
          loading={sending}
          disabled={selectedEmployees.length === 0}
        >
          {sending ? 'Sending Invites...' : `Send Invites (${selectedEmployees.length})`}
        </Button>
      </div>
      {/* Alert Popup */}
      <CustomAlert
        isOpen={alertState.show}
        onClose={hideAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
      />
    </div>
  );
};

export default SendOrientationInvites;
